<template>
  <div class="blog">
    <!-- Blog Header -->
    <div class="jumbotron-fluid bg-ctdark py-4">
      <div class="container">
        <div class="row">
          <div class="col-md-12 col-lg-4"></div>
          <div class="col-md-12 col-lg-4 text-center pb-2">
            <h1 class="text-blog-title text-light pt-0">{{ t('blog.title') }}</h1>
          </div>
          <div class="col-md-12 col-lg-4"></div>
        </div>
      </div>
    </div>

    <!-- Blog Content -->
    <div class="jumbotron-fluid py-3 bg-ctmain">
      <div class="container">
        <div class="row">
          <!-- Blog Entries Column -->
          <div class="col-md-8 mt-4 left">
            <div v-if="loading" class="text-center p-5">
              <div class="spinner-border text-ctorange" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading blog posts...</p>
            </div>

            <div v-else-if="error" class="alert alert-danger">
              <p><strong>Error loading blog posts:</strong></p>
              <pre>{{ JSON.stringify(error, null, 2) }}</pre>
            </div>

            <div v-else-if="!validPosts || validPosts.length === 0" class="text-center p-5">
              <p>{{ t('blog.emptyMessage') }}</p>
              <button @click="retryFetch" class="btn btn-ctl mt-3">{{ t('blog.retry') }}</button>
            </div>

            <div v-else>
              <div v-for="post in validPosts" :key="post.id" class="card mb-4">
                <div class="card-body">
                  <template v-if="post.status === 0">
                    <h2 class="card-title text-secondary font-italic">(draft) {{ getTranslatedTitle(post) }}</h2>
                  </template>
                  <template v-else>
                    <h2 class="card-title">
                      <router-link :to="{ name: 'blog-post', params: { slug: post.slug } }" class="text-decoration-none text-white">
                        {{ getTranslatedTitle(post) }}
                      </router-link>
                    </h2>
                  </template>
                  <p class="card-text text-muted h6">{{ formatDate(post.created_on) }}</p>
                  <div class="card-text text-white">{{ truncateContent(getTranslatedContent(post)) }}</div>
                  <router-link :to="{ name: 'blog-post', params: { slug: post.slug } }" class="btn btn-ctl mt-3">
                    {{ t('blog.readMore') }} &rarr;
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar -->
          <div class="col-md-4 float-right mt-4">
            <div class="card my-4">
              <h5 class="card-header">{{ t('blog.recentPosts') }}</h5>
              <div v-if="validPosts.length > 0">
                <div v-for="post in recentValidPosts" :key="post.id" class="card-body py-2">
                  <p class="card-text">
                    <router-link :to="{ name: 'blog-post', params: { slug: post.slug } }" class="text-decoration-none text-white">
                      {{ getTranslatedTitle(post) }}
                    </router-link>
                    <span class="font-italic small ms-2 text-muted">{{ formatDate(post.created_on) }}</span>
                  </p>
                </div>
              </div>
              <div v-else class="card-body">
                <p class="text-muted">{{ t('blog.emptyMessage') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { mapState, mapActions} from 'vuex';

export default {
  name: 'BlogView',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      debugMode: false
    };
  },
  computed: {
    ...mapState('blog', ['posts', 'loading', 'error']),
    validPosts() {
      // Filter out any null or invalid posts
      return this.posts && Array.isArray(this.posts)
          ? this.posts.filter(post => post && post.id)
          : [];
    },
    recentValidPosts() {
      return [...this.validPosts]
          .sort((a, b) => new Date(b.created_on) - new Date(a.created_on))
          .slice(0, 5);
    }
  },
  methods: {
    ...mapActions('blog', ['fetchPosts']),
    truncateContent(content) {
      if (!content) return '';

      // Create a temporary div to handle HTML content
      const div = document.createElement('div');
      div.innerHTML = content;

      // Get the text content
      let text = div.textContent || div.innerText || '';

      // Truncate to 300 characters
      if (text.length > 300) {
        text = text.substring(0, 300) + '...';
      }

      return text;
    },
    formatDate(dateString) {
      if (!dateString) return 'Unknown date';
      try {
        const options = {year: 'numeric', month: 'long', day: 'numeric'};
        return new Date(dateString).toLocaleDateString(this.$i18n.locale, options);
      } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
      }
    },
    retryFetch() {
      console.log('Retrying fetch of blog posts...');
      this.fetchPosts().catch(error => {
        console.error('Error retrying blog posts fetch:', error);
      });
    },
    getTranslatedTitle(post) {
      const currentLanguage = this.$i18n.locale;
      if (currentLanguage !== post.language) {
        const translation = post.translations?.find(t => t.language === currentLanguage);
        if (translation) {
          return translation.title;
        }
      }
      return post.title;
    },
    getTranslatedContent(post) {
      const currentLanguage = this.$i18n.locale;
      if (currentLanguage !== post.language) {
        const translation = post.translations?.find(t => t.language === currentLanguage);
        if (translation) {
          return translation.content;
        }
      }
      return post.content;
    }
  },
  created() {
    console.log('BlogView component created');
    this.setPageTitle(this.t('blog.title'));

    // Update the API language parameter based on current locale
    this.fetchPosts({ lang: this.$i18n.locale }).then(() => {
      console.log('Blog posts fetched successfully:', this.posts);
    }).catch(error => {
      console.error('Error fetching blog posts:', error);
    });
  },
  watch: {
    // If the language changes, reload posts
    '$i18n.locale': function(newLocale) {
      this.fetchPosts({ lang: newLocale });
    }
  }
}
</script>

<style scoped>
.text-blog-title {
  font-size: 2.5rem;
  font-weight: 700;
}

.card {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: var(--ct-dark-tertiary);
}

.card-header {
  background-color: var(--ct-dark);
  color: white;
}

.card-body {
  color: #e0e0e0;
}

.card-title {
  color: white;
}

.card-text {
  color: #e0e0e0;
}

.btn-ctl {
  background-color: var(--ct-orange);
  color: #000;
  font-weight: 600;
}

.btn-ctl:hover {
  background-color: var(--ct-orange-hover);
}

/* For content */
.card-text :deep(img) {
  max-width: 100%;
  height: auto;
}

.card-text :deep(table) {
  width: 100%;
  margin-bottom: 1rem;
  border-collapse: collapse;
}

.card-text :deep(td), .card-text :deep(th) {
  padding: 0.5rem;
  border: 1px solid #dee2e6;
}

.text-white {
  color: #ffffff !important;
}
</style>